import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  FormHelperText,
  Chip
} from "@material-ui/core";
import { Formik } from "formik";
import * as Yup from "yup";
import moment from "moment-timezone";
import _ from "lodash";
import CustomDialog from '../../../Components/CustomDialog'
import { AppButton, DisplayMessage, Loader } from "../../../Components";
import './style'
import useStyles from "./style"
import DateTimePicker from '../../../elements/DateTimePicker'
import { downTimeTypes } from './constants'
import { developerHubService } from '../../../Services/DeveloperHubService/index'
import * as messageTypes from '../../../utils/messageTypes'

const DowntimeForm = (props) => {
  const [startCalendarOpen, setStartCalendarOpen] = useState(false)
  const [endCalendarOpen, setEndCalendarOpen] = useState(false);
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState()
  const [messageType, setMessageType] = useState()
  const [apisAffectedList, setApisAffectedList] = useState([])
  const { accountId } = props
  const classes = useStyles()
  const CalendarFormat = 'MM-DD-YYYY hh:mm A'

  const initialTime = moment.tz(props.timezone)

  const { timezone } = props;

  const initialValues = {
    startDateTime: "",
    endDateTime: "",
    downtimeReason: "",
    downtimeType: "",
    apiURLs: []
  };

  const validationSchema = Yup.object({
    startDateTime: Yup.string().required("Start Time is Required"),
    endDateTime: Yup.string()
      .required("End Time is Required")
      .test(
        "is-after-start",
        "End time must be after Start time",
        function (value) {
          const { startDateTime } = this.parent;

          if (!startDateTime || !value) {
            return true;
          }

          return moment(value, CalendarFormat).isAfter(
            moment(startDateTime, CalendarFormat)
          );
        }
      ),
    downtimeReason: Yup.string().trim().required("Reason is required"),
    downtimeType: Yup.string().required("Type is required"),
    apiURLs: Yup.array().min(1, "Api selection is required")
  });

  const handleClose = () => {
    props.setDowntimeForm(prev => !prev)
  }

  const getApiUrl = () => {
    setLoading(true)
    developerHubService.getApiUrls(accountId)
    .then((response) => {
      setApisAffectedList(["ALL", ...response ])
      setLoading(false)
    }).catch((error) => {
      setLoading(false)
      setOpen(true)
      setMessage(_.get(error, 'data.error.displayMessage', error.statusText))
      setMessageType(messageTypes.ERROR)
    })
  }

  useEffect(() => {
    if (props.open) {
      getApiUrl()
    }
  }, [props.open])

  const submitForm = (values, { resetForm }) => {
    const payload = {
      ...values,
      startDateTime: moment(values.startDateTime, CalendarFormat).format(
        "YYYY-MM-DD HH:mm:ss"
      ),
      endDateTime: moment(values.endDateTime, CalendarFormat).format(
        "YYYY-MM-DD HH:mm:ss"
      )
    };

    setLoading(true);
    developerHubService
      .postDownTimeDate(accountId, payload)
      .then(() => {
        setLoading(false);
        setOpen(true);
        setMessage("Downtime Addition Successful");
        setMessageType(messageTypes.SUCCESS);
        resetForm();
      })
      .catch((error) => {
        setLoading(false);
        setOpen(true);
        setMessage(_.get(error, "data.error.displayMessage", error.statusText));
        setMessageType(messageTypes.ERROR);
      });
  };

  return (
    <>
      <Loader loading={loading} />
      <DisplayMessage
        open={open}
        message={message}
        messageType={messageType}
        handleClose={() => setOpen(false)}
      />

      <CustomDialog
        open={props.open}
        handleClose={handleClose}
        title="Downtime Form"
        content={
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={submitForm}
          >
            {({
              values,
              errors,
              touched,
              setFieldValue,
              handleSubmit
            }) => (
              <form onSubmit={handleSubmit}>
                <Box className={classes.downtimeCalendarWrapper} >
                  <Box mb={2}>
                    <TextField
                      label="Downtime Start Time*"
                      value={values.startDateTime}
                      onClick={() => setStartCalendarOpen(true)}
                      error={touched.startDateTime && !!errors.startDateTime}
                      helperText={
                        touched.startDateTime && errors.startDateTime
                      }
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                    {startCalendarOpen && (
                      <DateTimePicker
                        value={
                          values.startDateTime ||
                          initialTime.format(CalendarFormat)
                        }
                        format={CalendarFormat}
                        timezone={timezone}
                        handleCancel={() => setStartCalendarOpen(false)}
                        handleDone={(data) => {
                          setStartCalendarOpen(false);
                          setFieldValue("startDateTime", data.dateTime);
                        }}
                        minDate={moment().subtract(6, "days").toDate()}
                        maxDate={new Date()}
                      />
                    )}
                  </Box>

                  <Box mb={2}>
                    <TextField
                      label="Downtime End Time*"
                      value={values.endDateTime}
                      onClick={() => setEndCalendarOpen(true)}
                      disabled={!values.startDateTime}
                      error={touched.endDateTime && !!errors.endDateTime}
                      helperText={touched.endDateTime && errors.endDateTime}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                    {endCalendarOpen && (
                      <DateTimePicker
                        value={
                          values.endDateTime ||
                          initialTime.format(CalendarFormat)
                        }
                        format={CalendarFormat}
                        timezone={timezone}
                        minDate={moment(values.startDateTime, CalendarFormat)}
                        maxDate={new Date()}
                        handleCancel={() => setEndCalendarOpen(false)}
                        handleDone={(data) => {
                          setEndCalendarOpen(false);
                          setFieldValue("endDateTime", data.dateTime);
                        }}
                      />
                    )}
                  </Box>

                  <Box mb={2}>
                    <TextField
                      label="Downtime Reason*"
                      value={values.downtimeReason}
                      onChange={(e) =>
                        setFieldValue(
                          "downtimeReason",
                          e.target.value.trimStart()
                        )
                      }
                      error={
                        touched.downtimeReason &&
                        !!errors.downtimeReason
                      }
                      helperText={
                        touched.downtimeReason && errors.downtimeReason
                      }
                      fullWidth
                    />
                  </Box>

                  <Box mb={2}>
                    <FormControl
                      fullWidth
                      error={touched.downtimeType && !!errors.downtimeType}
                    >
                      <InputLabel>Downtime Type*</InputLabel>
                      <Select
                        value={values.downtimeType}
                        onChange={(e) =>
                          setFieldValue("downtimeType", e.target.value)
                        }
                        renderValue={(selected) => (
                          <Chip label={selected} />
                        )}
                      >
                        {downTimeTypes.map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {touched.downtimeType && errors.downtimeType}
                      </FormHelperText>
                    </FormControl>
                  </Box>

                  <Box mb={2}>
                    <FormControl
                      fullWidth
                      error={touched.apiURLs && !!errors.apiURLs}
                    >
                      <InputLabel>Api's Affected*</InputLabel>
                      <Select
                        multiple
                        value={values.apiURLs}
                        onChange={(e) =>
                          setFieldValue("apiURLs", e.target.value)
                        }
                        renderValue={(selected) => (
                          <Box className={classes.selectWrapper}>
                            {selected.map((id) => (
                              <Chip key={id} label={id} />
                            ))}
                          </Box>
                        )}
                      >
                        {apisAffectedList.map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {touched.apiURLs && errors.apiURLs}
                      </FormHelperText>
                    </FormControl>
                  </Box>

                  <Box display="flex" justifyContent="flex-end" mt={3}>
                    <AppButton
                      text="Submit"
                      fill
                      type="submit"
                      className={classes.button}
                    />
                  </Box>
                </Box>
              </form>
            )}
          </Formik>
        }
      />
    </>
  );
}

const mapStateToProps = ({auth}) => {
  const { accountId } = auth 
  return { accountId } 
}

export default connect(mapStateToProps)(DowntimeForm)